import React, { useEffect, useState } from 'react';
import api from '../services/api';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;

const monthOptions = Array.from({ length: 12 }, (_, idx) => idx + 1);

const formatNumber = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('ko-KR');
};

const tableContainer = {
  marginTop: '20px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle = {
  background: '#f8fafc',
  borderBottom: '1px solid #d0d7de',
  padding: '10px',
  textAlign: 'center',
  fontWeight: 600,
};

const tdStyle = {
  borderBottom: '1px solid #eef2f6',
  padding: '10px',
  textAlign: 'center',
};

const filterGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const labelStyle = { fontWeight: 600 };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d0d7de',
};

const buttonStyle = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const secondaryButton = {
  ...buttonStyle,
  background: '#475569',
};

const logBox = {
  marginTop: '24px',
  padding: '12px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#f8fafc',
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContent = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  width: '90%',
  maxWidth: '420px',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.25)',
};

const modalTitle = {
  fontSize: '1.1rem',
  fontWeight: 700,
  marginBottom: '10px',
  color: '#1f2937',
};

const modalLabel = {
  display: 'block',
  fontWeight: 600,
  marginBottom: '4px',
  color: '#334155',
};

const modalHint = {
  marginTop: '12px',
  fontSize: '0.84rem',
  color: '#64748b',
  lineHeight: 1.5,
};

const modalActions = {
  marginTop: '24px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
};

function PayrollSendPage() {
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [nameKeyword, setNameKeyword] = useState('');

  const [payrolls, setPayrolls] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [openingPdf, setOpeningPdf] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  const fetchProjects = async (targetYear = year) => {
    try {
      const response = await api.get('/projects', { params: { year: targetYear } });
      setProjects(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPayrolls = async (overrides = {}) => {
    const effectiveProjectId = overrides.projectId ?? projectId;
    setLoading(true);
    try {
      const response = await api.get('/payroll', {
        params: {
          year,
          month,
          projectId: effectiveProjectId || undefined,
          name: nameKeyword || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        },
      });
      setPayrolls(response.data || []);
      setSelectedIds([]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (overrides = {}) => {
    const effectiveProjectId = overrides.projectId ?? projectId;
    setLogLoading(true);
    try {
      const response = await api.get('/payroll/logs', {
        params: {
          year,
          month,
          projectId: effectiveProjectId || undefined,
          name: nameKeyword || undefined,
        },
      });
      setLogs(response.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLogLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(year);
  }, [year]);

  useEffect(() => {
    if (!viewTarget) {
      setCopyMessage('');
    }
  }, [viewTarget]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = () => {
    fetchPayrolls();
    fetchLogs();
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(payrolls.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleItem = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      setSendResult({ type: 'error', text: '발송할 대상을 선택해주세요.' });
      return;
    }
    try {
      setSendResult(null);
      setLoading(true);
      const response = await api.post('/payroll/send', { payrollIds: selectedIds });
      const errors = response.data.errors || [];
      setSendResult({
        type: errors.length > 0 ? 'warning' : 'success',
        text: `총 ${response.data.queued}건이 발송 대기열에 등록되었습니다.` + (errors.length ? ` (실패 ${errors.length}건)` : ''),
        errors,
      });
      fetchLogs();
    } catch (error) {
      const message = error.response?.data?.message || '발송 처리 중 오류가 발생했습니다.';
      setSendResult({ type: 'error', text: message });
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (payrollId) => {
    try {
      setOpeningPdf(true);
      const response = await api.get(`/payroll/${payrollId}/payslip`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000 * 60);
      setOpeningPdf(false);
      setViewTarget(null);
    } catch (error) {
      const message = error.response?.data?.message || '급여명세표를 불러오지 못했습니다.';
      setSendResult({ type: 'error', text: message });
      setOpeningPdf(false);
    }
  };

  const summary = {
    total: payrolls.length,
    selected: selectedIds.length,
    totalNet: payrolls.reduce((sum, item) => sum + Number(item.netPay || 0), 0),
  };

  const resultColors = {
    success: { background: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
    warning: { background: '#fffbeb', color: '#b45309', border: '#fcd34d' },
    error: { background: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
  };

  const extractBirth = (rrn) => {
    if (!rrn) return '';
    const digits = rrn.replace(/\D/g, '');
    return digits.slice(0, 6);
  };

  const handleCopyBirth = async (birth) => {
    if (!birth) return;
    const fallbackCopy = (text) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.top = '-1000px';
      textarea.style.left = '-1000px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      let success = false;
      try {
        success = document.execCommand('copy');
      } catch (error) {
        success = false;
      }
      document.body.removeChild(textarea);
      return success;
    };

    const showMessage = (text, isError = false) => {
      setCopyMessage(text);
      setTimeout(() => setCopyMessage(''), 1500);
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(birth);
        showMessage('복사되었습니다.');
        return;
      }
      const fallbackResult = fallbackCopy(birth);
      showMessage(fallbackResult ? '복사되었습니다.' : '복사에 실패했습니다.', !fallbackResult);
    } catch (error) {
      const fallbackResult = fallbackCopy(birth);
      showMessage(fallbackResult ? '복사되었습니다.' : '복사에 실패했습니다.', !fallbackResult);
    }
  };

  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [logNameFilter, setLogNameFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('all');

  const handleToggleLogAll = (checked, targetLogs) => {
    const base = targetLogs || logs;
    if (checked) {
      setSelectedLogIds(base.map((log) => log.id));
    } else {
      setSelectedLogIds([]);
    }
  };

  const handleToggleLogItem = (id) => {
    setSelectedLogIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleDeleteLogs = async () => {
    if (selectedLogIds.length === 0) {
      setSendResult({ type: 'error', text: '삭제할 발송 이력을 선택해주세요.' });
      return;
    }
    if (!window.confirm('선택한 발송 이력을 삭제하시겠습니까?')) return;
    try {
      setLogLoading(true);
      await api.post('/payroll/logs/delete', { logIds: selectedLogIds });
      setSelectedLogIds([]);
      fetchLogs();
    } catch (error) {
      const message = error.response?.data?.message || '발송 이력 삭제 중 오류가 발생했습니다.';
      setSendResult({ type: 'error', text: message });
    } finally {
      setLogLoading(false);
    }
  };

  const stickyBarStyle = {
    position: isMobile ? 'static' : 'sticky',
    top: isMobile ? 'auto' : 60,
    zIndex: isMobile ? 'auto' : 5,
    background: '#ffffff',
    paddingBottom: 8,
    borderBottom: '1px solid #e5e7eb',
  };

  const statusLabels = {
    draft: '임시저장',
    ready: '발송준비',
    queued: '발송대기',
    processing: '발송중',
    sent: '발송성공',
    success: '발송성공',
    failed: '발송실패',
  };

  const renderStatus = (value) => statusLabels[value] || value || '-';

  // 발송 결과(로그) 검색 필터 적용
  const filteredLogs = logs.filter((log) => {
    const name = log.Payroll?.Participant?.name || '';
    const status = log.status || '';

    const matchName =
      !logNameFilter || name.toLowerCase().includes(logNameFilter.toLowerCase());

    const matchStatus =
      logStatusFilter === 'all' || status === logStatusFilter;

    return matchName && matchStatus;
  });

  return (
    <div>
      <div style={stickyBarStyle}>
        <h2 style={{ marginBottom: '6px' }}>급여 명세표 발송</h2>
        <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '10px' }}>
          참여자 급여등록에서 급여 또는 계좌/이메일 정보를 수정하면, 발송 시점에 명세표 PDF가 자동으로 다시 생성되어 최신 내용이 반영됩니다.
        </div>

        <div style={filterGroup}>
          <label style={labelStyle}>
            연도
            <input
              type="number"
              value={year}
              min="2000"
              style={{ ...inputStyle, marginLeft: '6px', width: '110px' }}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>

          <label style={labelStyle}>
            월
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            사업단
            <select
              value={projectId}
              onChange={(e) => {
                const nextProjectId = e.target.value;
                setProjectId(nextProjectId);
                fetchPayrolls({ projectId: nextProjectId });
                fetchLogs({ projectId: nextProjectId });
              }}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="">전체</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectName}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            성명
            <input
              type="text"
              value={nameKeyword}
              style={{ ...inputStyle, marginLeft: '6px' }}
              placeholder="성명 검색"
              onChange={(e) => setNameKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </label>

          <label style={labelStyle}>
            상태
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="all">전체</option>
              <option value="ready">발송준비</option>
              <option value="queued">발송대기</option>
              <option value="sent">발송성공</option>
              <option value="failed">발송실패</option>
              <option value="draft">임시저장</option>
            </select>
          </label>

          <button style={secondaryButton} onClick={handleSearch} disabled={loading}>
            조회
          </button>
          <button style={{ ...buttonStyle, marginLeft: '8px' }} onClick={handleSend} disabled={loading}>
            선택 급여명세표 발송
          </button>
        </div>
      </div>

      <div
        style={{
          marginBottom: '12px',
          color: '#475569',
          fontSize: '0.9rem',
        }}
      >
        선택 {summary.selected}건 / 총 {summary.total}건 | 선택된 차감지급액 합계:{' '}
        <strong>{formatNumber(summary.totalNet)}원</strong>
      </div>

      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === payrolls.length && payrolls.length > 0}
                  onChange={(e) => handleToggleSelectAll(e.target.checked)}
                />
              </th>
              <th style={thStyle}>성명</th>
              <th style={thStyle}>사업단</th>
              <th style={thStyle}>차감지급액</th>
              <th style={thStyle}>은행</th>
              <th style={thStyle}>계좌번호</th>
              <th style={thStyle}>이메일</th>
              <th style={thStyle}>생년월일(비밀번호)</th>
              <th style={thStyle}>상태</th>
              <th style={thStyle}>명세표 보기</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={10}>
                  불러오는 중...
                </td>
              </tr>
            ) : payrolls.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={10}>
                  발송 가능한 데이터가 없습니다.
                </td>
              </tr>
            ) : (
              payrolls.map((item) => (
                <tr key={item.id}>
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => handleToggleItem(item.id)}
                    />
                  </td>
                  <td style={tdStyle}>{item.Participant?.name}</td>
                  <td style={tdStyle}>{item.Project?.projectName || '-'}</td>
                  <td style={tdStyle}>{formatNumber(item.netPay)}</td>
                  <td style={tdStyle}>{item.bankName || item.Participant?.bankName || '-'}</td>
                  <td style={tdStyle}>{item.bankAccount || item.Participant?.bankAccount || '-'}</td>
                  <td style={tdStyle}>{item.email || item.Participant?.email || '-'}</td>
                  <td style={tdStyle}>{extractBirth(item.Participant?.rrn) || '-'}</td>
                  <td style={tdStyle}>{renderStatus(item.status)}</td>
                  <td style={tdStyle}>
                    <button
                      style={{ ...secondaryButton, padding: '6px 10px', fontSize: '0.8rem' }}
                      onClick={() => setViewTarget(item)}
                    >
                      보기
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {sendResult && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            border: `1px solid ${resultColors[sendResult.type]?.border || '#cbd5f5'}`,
            background: resultColors[sendResult.type]?.background || '#eff6ff',
            color: resultColors[sendResult.type]?.color || '#1d4ed8',
          }}
        >
          {sendResult.text}
          {sendResult.errors && sendResult.errors.length > 0 && (
            <ul style={{ marginTop: '6px', paddingLeft: '20px' }}>
              {sendResult.errors.map((error, idx) => (
                <li key={idx}>
                  {error.id}: {error.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div style={logBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>발송 결과</h3>
            <button style={secondaryButton} onClick={fetchLogs} disabled={logLoading}>
              발송 결과 새로고침
            </button>
            <button
              style={{ ...secondaryButton, background: '#dc2626' }}
              onClick={handleDeleteLogs}
              disabled={logLoading || selectedLogIds.length === 0}
            >
              선택 이력 지우기
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ ...labelStyle, fontSize: '0.85rem' }}>
              성명
              <input
                type="text"
                value={logNameFilter}
                onChange={(e) => setLogNameFilter(e.target.value)}
                placeholder="발송 결과 성명 검색"
                style={{ ...inputStyle, marginLeft: '4px', padding: '6px 8px' }}
              />
            </label>
            <label style={{ ...labelStyle, fontSize: '0.85rem' }}>
              상태
              <select
                value={logStatusFilter}
                onChange={(e) => setLogStatusFilter(e.target.value)}
                style={{ ...inputStyle, marginLeft: '4px', padding: '6px 8px' }}
              >
                <option value="all">전체</option>
                <option value="ready">발송준비</option>
                <option value="queued">발송대기</option>
                <option value="processing">발송중</option>
                <option value="sent">발송성공</option>
                <option value="success">발송성공(success)</option>
                <option value="failed">발송실패</option>
                <option value="draft">임시저장</option>
              </select>
            </label>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              {logLoading
                ? '갱신 중...'
                : `총 ${logs.length}건 / 검색 ${filteredLogs.length}건`}
            </span>
          </div>
        </div>
        <div style={{ marginTop: '12px' }}>
          {logs.length === 0 ? (
            <div style={{ color: '#94a3b8' }}>발송 이력이 없습니다.</div>
          ) : (
            <table style={{ ...tableStyle, marginTop: '8px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    <input
                      type="checkbox"
                      checked={
                        filteredLogs.length > 0 &&
                        selectedLogIds.length > 0 &&
                        selectedLogIds.length === filteredLogs.length
                      }
                      onChange={(e) => handleToggleLogAll(e.target.checked, filteredLogs)}
                    />
                  </th>
                  <th style={{ ...thStyle, width: '140px' }}>성명</th>
                  <th style={{ ...thStyle, width: '220px' }}>사업단</th>
                  <th style={{ ...thStyle, width: '120px' }}>상태</th>
                  <th style={{ ...thStyle, width: '200px' }}>발송시간</th>
                  <th style={{ ...thStyle }}>이메일</th>
                  <th style={{ ...thStyle, width: '180px' }}>비고</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={selectedLogIds.includes(log.id)}
                        onChange={() => handleToggleLogItem(log.id)}
                      />
                    </td>
                    <td style={{ ...tdStyle, width: '140px' }}>
                      {log.Payroll?.Participant?.name}
                    </td>
                    <td style={{ ...tdStyle, width: '220px' }}>
                      {log.Payroll?.Project?.projectName || '-'}
                    </td>
                    <td style={{ ...tdStyle, width: '120px' }}>{renderStatus(log.status)}</td>
                    <td style={{ ...tdStyle, width: '200px' }}>
                      {log.sentAt ? new Date(log.sentAt).toLocaleString() : '-'}
                    </td>
                    <td style={tdStyle}>{log.email}</td>
                    <td style={{ ...tdStyle, width: '180px', color: '#dc2626' }}>
                      {log.errorMessage || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewTarget && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={modalTitle}>PDF 비밀번호 안내</div>
            <div style={{ fontSize: '0.95rem', color: '#1f2937' }}>
              내부 확인용으로 명세표를 열 경우 비밀번호가 필요합니다.
            </div>
            <div style={{ marginTop: '16px' }}>
              <span style={modalLabel}>참여자</span>
              {viewTarget.Participant?.name || '-'}
            </div>
            <div style={{ marginTop: '14px' }}>
              <span style={modalLabel}>생년월일(비밀번호)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>
                  {extractBirth(viewTarget.Participant?.rrn) || '정보 없음'}
                </span>
                {extractBirth(viewTarget.Participant?.rrn) && (
                  <button
                    style={{
                      ...secondaryButton,
                      background: '#e2e8f0',
                      color: '#0f172a',
                      padding: '6px 10px',
                      fontSize: '0.8rem',
                    }}
                    onClick={() => handleCopyBirth(extractBirth(viewTarget.Participant?.rrn))}
                  >
                    복사
                  </button>
                )}
              </div>
              {copyMessage && (
                <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#059669' }}>{copyMessage}</div>
              )}
            </div>
            <div style={modalHint}>
              비밀번호는 YYMMDD 형식의 생년월일 6자리입니다.
            </div>
            <div style={modalActions}>
              <button
                style={{ ...secondaryButton, background: '#94a3b8' }}
                onClick={() => setViewTarget(null)}
              >
                닫기
              </button>
              <button
                style={{ ...buttonStyle, marginTop: 0, opacity: openingPdf ? 0.7 : 1 }}
                onClick={() => handleViewPayslip(viewTarget.id)}
                disabled={openingPdf}
              >
                {openingPdf ? '열기 중...' : 'PDF 열기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayrollSendPage;

